import { useState } from 'react';
import { CheckCircle2, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PaymentModal } from '@/components/PaymentModal';
import { formatPrice } from '@/lib/stripe';
import type { QuestionSetDef } from '@/lib/question-sets';
import { cn } from '@/lib/utils';

interface QuestionSetPromoCardProps {
  /** The pack to offer. See QUESTION_SETS in src/lib/question-sets.ts */
  questionSet: QuestionSetDef;
  onPurchaseSuccess?: () => void;
  hasPurchased?: boolean;
  className?: string;
  variant?: 'default' | 'compact' | 'featured';
  /** Hide the price (used on marketing surfaces like the landing page). */
  showPrice?: boolean;
}

/**
 * Generic promo card for a purchasable question pack.
 *
 * One component serves every pack in QUESTION_SETS — the pack's name, price,
 * icon and copy all come from its QuestionSetDef, so adding a pack needs no
 * new component. (PreMarriagePromoCard and ValentinePromoCard predate this and
 * remain separate.)
 */
export function QuestionSetPromoCard({
  questionSet,
  onPurchaseSuccess,
  hasPurchased = false,
  className,
  variant = 'default',
  showPrice = true,
}: QuestionSetPromoCardProps) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const Icon = questionSet.icon;
  const priceDisplay = formatPrice(questionSet.amount, questionSet.currency);

  const paymentModal = (
    <PaymentModal
      open={showPaymentModal}
      onOpenChange={setShowPaymentModal}
      productId={questionSet.id}
      amount={questionSet.amount}
      productName={questionSet.name}
      productDescription={questionSet.description}
      onSuccess={() => onPurchaseSuccess?.()}
    />
  );

  if (variant === 'compact') {
    return (
      <>
        <Card
          className={cn(
            'overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 dark:from-primary/10 dark:to-accent/10 dark:border-primary/30',
            className
          )}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{questionSet.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {questionSet.questionCount} curated questions
                  </p>
                </div>
              </div>
              {hasPurchased ? (
                <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  Owned
                </Badge>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  {showPrice ? priceDisplay : 'View'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        {paymentModal}
      </>
    );
  }

  if (variant === 'featured') {
    return (
      <>
        <Card
          className={cn(
            'relative overflow-hidden border-2 border-primary/30 dark:border-primary/40',
            className
          )}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-background dark:from-primary/10 dark:via-accent/10 dark:to-background" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-accent/10 to-transparent rounded-tr-full" />

          <div className="relative p-6 md:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  {questionSet.name}
                </h2>
                <p className="text-muted-foreground max-w-md">{questionSet.description}</p>
              </div>
              <div className="hidden md:flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent shadow-lg">
                <Icon className="h-8 w-8 text-white" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {questionSet.features.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0" />
                  <span className="text-foreground">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              {hasPurchased ? (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">You own this collection!</span>
                </div>
              ) : (
                <>
                  <Button
                    size="lg"
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
                  >
                    <Gift className="mr-2 h-5 w-5" />
                    {showPrice ? `Get for ${priceDisplay}` : 'Get'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center sm:text-left">
                    One-time purchase. Integrated into your Love Contract.
                  </p>
                </>
              )}
            </div>
          </div>
        </Card>
        {paymentModal}
      </>
    );
  }

  // Default variant
  return (
    <>
      <Card
        className={cn(
          'relative overflow-hidden border-primary/20 dark:border-primary/30',
          className
        )}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-background dark:from-primary/10 dark:via-accent/10 dark:to-background" />

        <div className="relative p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent shadow-md">
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>

          <h3 className="text-xl font-bold text-foreground mb-2">{questionSet.name}</h3>
          <p className="text-sm text-muted-foreground mb-4">{questionSet.description}</p>

          <ul className="space-y-2 mb-6">
            {questionSet.features.slice(0, 3).map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          {hasPurchased ? (
            <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Already purchased</span>
            </div>
          ) : (
            <div className="space-y-3">
              {showPrice && (
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-foreground">{priceDisplay}</span>
                  <span className="text-sm text-muted-foreground">one-time</span>
                </div>
              )}
              <Button
                onClick={() => setShowPaymentModal(true)}
                className="w-full bg-primary hover:bg-primary/90 text-white"
              >
                <Icon className="mr-2 h-4 w-4" />
                Purchase Now
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                One-time purchase. Share with your partner.
              </p>
            </div>
          )}
        </div>
      </Card>
      {paymentModal}
    </>
  );
}
